import * as flexsearch from "flexsearch";

import { serverContext } from "js/config";
import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import AppController from "js/core/AppController";
import { getAllDocuments, search, getIndexConfig } from "common/flexsearch";
import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";

import DataService from "./DataService";

interface SlideTemplatesDataServiceSearchQuery {
    templateId?: string,
    fullTextSearchQuery?: string,
    size?: number,
    showHiddenTemplates?: boolean
}

interface SlideTemplatesDataServiceDataItem extends flexsearch.DocumentData {
    id: string,
    icon: string,
    title: string,
    description: string,
    category: string,
    tags: string[],
    tagsText: string,
    hideTemplate: boolean
}

class SlideTemplatesDataService extends DataService<any> {
    protected async _prepareIndex(workspaceId: string) {
        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(appVersion);

        const slideTemplatesData = Object.entries(slideTemplates as Record<string, any>)
            .map(([id, template]) => ({
                id,
                icon: template.icon,
                title: template.title,
                description: template.description,
                category: template.category,
                tags: template.tags?.split(" ") ?? [],
                tagsText: template.tags,
                hideTemplate: !!template.hideTemplate
            }));

        this._index = new flexsearch.Document<SlideTemplatesDataServiceDataItem>({
            ...getIndexConfig(serverContext.locale ?? "en", true),
            document: {
                id: "id",
                index: ["title", "description", "category", "tagsText"],
                store: ["icon", "title", "description", "category", "tagsText", "hideTemplate"]
            },
        });

        slideTemplatesData.forEach(template => this._index.add(template));
    }

    public async search(query: SlideTemplatesDataServiceSearchQuery): Promise<SlideTemplatesDataServiceDataItem[]> {
        await this.ensureIndexPrepared(AppController.workspaceId);

        const {
            templateId,
            fullTextSearchQuery,
            size = SlideTemplatesDataService.SEARCH_QUERY_SIZE,
            showHiddenTemplates
        } = query;

        if (templateId) {
            return [this._index.get(templateId)];
        }

        let searchResults: SlideTemplatesDataServiceDataItem[];
        if (fullTextSearchQuery) {
            searchResults = search({
                index: this._index,
                query: fullTextSearchQuery
            });
        } else {
            searchResults = getAllDocuments(this._index);
        }

        if (!showHiddenTemplates) {
            searchResults = searchResults.filter(doc => !doc.hideTemplate);
        }

        // Don't include authoring templates if we're not flagged to do so
        searchResults = searchResults.filter(template => (
            WorkspaceController.actionPermissions[ActionPermissionsObject.CLASSIC_SLIDES].use ||
            template.id !== "authoring"
        ));

        searchResults = searchResults.slice(0, size);

        return searchResults;
    }
}

export default SlideTemplatesDataService;
